import { useState, useEffect, useCallback } from 'react';
import { setStorageItem, getStorageItem, removeStorageItem } from '../utils/storage.js';

/**
 * 로컬 스토리지를 React 상태로 관리하는 커스텀 훅
 * @param {string} key - 스토리지 키
 * @param {any} initialValue - 초기값
 * @param {number} ttl - 만료 시간 (밀리초, 선택사항)
 * @returns {[any, Function, Function]} [값, 설정 함수, 삭제 함수]
 */
export const useLocalStorage = (key, initialValue, ttl = null) => {
  // 상태 초기화
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = getStorageItem(key, initialValue);
      return item;
    } catch (error) {
      console.error('로컬 스토리지 초기화 오류:', error);
      return initialValue;
    }
  });

  // 값 설정 함수
  const setValue = useCallback((value) => {
    try {
      // 함수인 경우 이전 값을 인자로 받아서 처리
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // 상태 업데이트
      setStoredValue(valueToStore);
      
      // 로컬 스토리지에 저장
      setStorageItem(key, valueToStore, ttl);
    } catch (error) {
      console.error('로컬 스토리지 설정 오류:', error);
    }
  }, [key, storedValue, ttl]);

  // 값 삭제 함수
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      removeStorageItem(key);
    } catch (error) {
      console.error('로컬 스토리지 삭제 오류:', error);
    }
  }, [key, initialValue]);

  // 다른 탭에서 스토리지 변경 감지
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue);
          setStoredValue(newValue.value);
        } catch (error) {
          console.error('스토리지 변경 감지 오류:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue, removeValue];
};

export default useLocalStorage; 